import { Typography } from '@mui/material'
import { useEntitiesFromSearch } from 'api/search'
import TabRouting from 'components/Tabs/TabRouting'
import useDrawerTabs from 'hooks/useDrawerTabs'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { DrawerLayout, ExpandCollapseBar } from 'theme/components'
import { Organization, OrganizationTableItem } from 'types'
import { useUpdateNotes } from '../../api/aws/checklists/useUpdateNotes'
import { useContact } from '../../api/aws/useContact'
import useTotals from '../../api/customHooks/useTotals'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import AccountingDrawerHeader from './AccountingDrawerHeader'

interface AccountingDrawerProps {
  open?: boolean
  onClose?: () => void
}
const AccountingDrawer = ({ open: _open, onClose }: AccountingDrawerProps) => {
  const { PK, SK } = useParams()
  const isAccountingDetail = Boolean(PK)
  const { buildTabs } = useDrawerTabs()
  const dealId = SK?.split('DEAL_')?.[1]
  const location = useLocation()
  const navigate = useNavigate()
  const { pinned } = useLayoutContext()
  const { deals, products, organizations } = useEntitiesFromSearch()
  // const newDeal = deals?.find(d => d?.SK === SK)
  const deal: OrganizationTableItem = useMemo(() => {
    if (!Array.isArray(deals) || !deals?.length) return null
    const d = deals?.find(d => d?.SK === SK)
    return d ? { ...d, PK: d?.PK, SK: d?.SK, EntityType: d?.EntityType } : null
  }, [deals, SK])
  const productRows = products?.filter(p => p?.deal_uuid === dealId)
  const { showSnackbar } = useSnackbarContext()
  const [activeTab, setActiveTab] = useState(0)
  const { mutate: updateNotes } = useUpdateNotes()
  const organization = organizations?.find(o => o?.PK === PK)
  const [expanded, setExpanded] = useState(false)
  const {
    data: billingContact,
    isLoading: isBillingContactLoading,
    isError: isBillingContactError,
  } = useContact(organization?.billingContact?.[0], ['accounting', 'billingContact', organization?.billingContact?.[0]])

  const totals = useTotals(productRows, deal?.implementationFee ?? 0, deal?.type)

  useEffect(() => {
    if (!organization) return

    if (location.pathname.includes(`/accounting/${PK}/${SK}/comments`)) {
      setActiveTab(1)
    } else {
      setActiveTab(0)
    }
  }, [location.pathname, organization])

  const taskSKs = useMemo(() => [`COMMENT_${dealId}`], [dealId]) as string[] // for filtering/retrieving existing notes
  const tabs = useMemo(
    () =>
      buildTabs({
        common: { deal, organization, expanded },
        tabs: [
          {
            type: 'financial',
            props: {
              deal,
              productRows: productRows,
              containerHeight: expanded ? 'calc(100vh - 210px)' : '100vh',
              hideViewIcon: true,
            },
          },
          {
            type: 'comments',
            props: {
              taskSKs: taskSKs,
              context: 'accounting',
              contextFilters: ['accounting'],
              checklistItems: [],
            },
          },
        ],
      }),
    [deal, productRows, expanded /* ...other deps */]
  )
  if (isBillingContactError) {
    return <Typography color="error">Error loading organization or billing contact.</Typography>
  }

  return (
    <DrawerLayout
      open={isAccountingDetail}
      onClose={onClose}
      width="75.5%"
      expanded={expanded}
      onExpandedChange={setExpanded}
      collapsedHeader={
        <ExpandCollapseBar entity={organization as Organization} isExpanded={expanded} onClose={onClose} />
      }
      expandedHeader={
        <AccountingDrawerHeader
          organization={organization}
          billingContact={billingContact}
          newDeal={deal}
          totals={totals}
        />
      }
    >
      <TabRouting key={PK} config={tabs} />
    </DrawerLayout>
  )
}

export default AccountingDrawer
